/** RTK Query endpoints and shared cache helpers for project architecture diagrams. */
/* eslint-disable max-lines-per-function */
import { useEffect, useRef } from 'react';

import type { AppDispatch } from '..';
import { eventService } from '../../services/eventService';
import { useAppDispatch } from '../hooks';

import { api } from './api';

export const ARCHITECTURE_TYPES = [
  'service_network',
  'sequence',
  'er_diagram',
  'backend_modules',
  'frontend_modules',
] as const;

export type DiagramType = typeof ARCHITECTURE_TYPES[number];

export interface Diagram {
  id: string;
  name: string;
  content: string;
  svg_content: string | null;
  render_order: string | null;
  version: number;
  updated_at: string | null;
}

export type DiagramBuckets = Record<DiagramType, Diagram[]>;

export interface ArchitectureDiagramLocation extends Diagram {
  diagramType: DiagramType;
  diagramIndex: number;
}

export const ARCHITECTURE_DIAGRAM_FALLBACK_LABELS: Record<DiagramType, string> = {
  service_network: 'Service Network',
  sequence: 'Sequence Diagram',
  er_diagram: 'Database Schema',
  backend_modules: 'Backend Modules',
  frontend_modules: 'Frontend Modules',
};

export type DiagramsResponse = DiagramBuckets & {
  project_id: string;
};

export interface UpdateArchitectureDiagramArgs {
  projectId: string;
  diagramType: DiagramType;
  name: string;
  content: string;
}

export interface UpdatedArchitectureDiagram {
  id: string;
  project_id: string;
  diagram_type: DiagramType;
  name: string;
  content: string;
  version: number;
}

export interface ArchitectureProposedOp {
  op: 'add' | 'update' | 'delete';
  id?: string;
  type?: string;
  name?: string;
  content?: string;
  rationale?: string;
}

export interface ArchitecturePendingProposalResponse {
  has_pending: boolean;
  operations: ArchitectureProposedOp[];
  summary?: string | null;
  source?: string | null;
  created_at?: string | null;
}

interface ArchitectureEventPayload {
  type?: string;
  project_id?: string;
  diagrams?: unknown;
}

interface ArchitectureQueryState {
  currentData?: DiagramsResponse;
  isError: boolean;
  isFetching: boolean;
}

interface ArchitectureStoreRef {
  dispatch: AppDispatch;
  getQueryState: () => ArchitectureQueryState;
}

const isRecord = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
);

export const toDiagramBuckets = (value: unknown): DiagramBuckets => {
  const source = isRecord(value) ? value : {};
  const bucket = (type: DiagramType): Diagram[] => (
    Array.isArray(source[type]) ? source[type] as Diagram[] : []
  );

  return {
    service_network: bucket('service_network'),
    sequence: bucket('sequence'),
    er_diagram: bucket('er_diagram'),
    backend_modules: bucket('backend_modules'),
    frontend_modules: bucket('frontend_modules'),
  };
};

const toDiagramsResponse = (projectId: string, value: unknown): DiagramsResponse => ({
  project_id: projectId,
  ...toDiagramBuckets(value),
});

export const selectAllArchitectureDiagrams = (
  response: Partial<DiagramBuckets> | undefined,
): Diagram[] => (
  response ? ARCHITECTURE_TYPES.flatMap((type) => response[type] ?? []) : []
);

/**
 * Return the diagrams that appear in the AI Overview architecture carousel.
 * Keeping the type and bucket index lets other viewers share the same order and
 * labels while still selecting the exact source diagram.
 */
export const selectArchitectureDiagramLocations = (
  response: Partial<DiagramBuckets> | undefined,
): ArchitectureDiagramLocation[] => (
  response
    ? ARCHITECTURE_TYPES.flatMap((diagramType) => (
      (response[diagramType] ?? []).map((diagram, diagramIndex) => ({
        ...diagram,
        diagramType,
        diagramIndex,
      }))
    )).filter((diagram) => Boolean(diagram.content))
    : []
);

export const getArchitectureDiagramLabel = (
  diagram: ArchitectureDiagramLocation,
  flatIndex: number,
): string => (
  diagram.name.trim()
  || ARCHITECTURE_DIAGRAM_FALLBACK_LABELS[diagram.diagramType]
  || `Diagram ${flatIndex + 1}`
);

export const hasArchitecture = (response: Partial<DiagramBuckets> | undefined): boolean => (
  selectAllArchitectureDiagrams(response).length > 0
);

interface WiredArchitectureProject {
  count: number;
  cleanup: () => void;
  cleanupTimer?: ReturnType<typeof setTimeout>;
}

const wiredProjects = new Map<string, WiredArchitectureProject>();

const invalidateArchitecture = (projectId: string, dispatch: AppDispatch) => {
  dispatch(api.util.invalidateTags([{ type: 'Architecture', id: projectId }]));
};

const releaseArchitectureSSE = (projectId: string) => {
  const current = wiredProjects.get(projectId);

  if (!current) return;
  current.count -= 1;
  if (current.count > 0) return;
  current.cleanupTimer = setTimeout(() => {
    const latest = wiredProjects.get(projectId);

    if (latest !== current || latest.count > 0) return;
    latest.cleanup();
    wiredProjects.delete(projectId);
  }, 0);
};

/** Keep one project-scoped Architecture listener regardless of consumer count. */
// eslint-disable-next-line max-statements
export function wireArchitectureSSE(
  projectId: string,
  storeRef: ArchitectureStoreRef,
): () => void {
  const existing = wiredProjects.get(projectId);

  if (existing) {
    if (existing.cleanupTimer) clearTimeout(existing.cleanupTimer);
    existing.cleanupTimer = undefined;
    existing.count += 1;
    eventService.connect(projectId);

    return () => releaseArchitectureSSE(projectId);
  }

  let versionInvalidationTimer: ReturnType<typeof setTimeout> | undefined;
  let ignoreNextConnected = !eventService.isConnectedTo(projectId);
  // eslint-disable-next-line max-statements
  const unsubscribeArchitecture = eventService.subscribe('architecture', (data: unknown) => {
    const payload = data as ArchitectureEventPayload;
    const belongsToProject = payload.project_id
      ? payload.project_id === projectId
      : eventService.isConnectedTo(projectId);

    if (payload.type !== 'architecture' || !belongsToProject) return;
    if (!isRecord(payload.diagrams)) {
      invalidateArchitecture(projectId, storeRef.dispatch);

      return;
    }

    storeRef.dispatch(architectureApi.util.upsertQueryData(
      'getArchitecture',
      projectId,
      toDiagramsResponse(projectId, payload.diagrams),
    ));
    if (versionInvalidationTimer) clearTimeout(versionInvalidationTimer);
    versionInvalidationTimer = setTimeout(() => {
      storeRef.dispatch(api.util.invalidateTags([
        { type: 'ArtifactVersion', id: `${projectId}:architecture` },
      ]));
    }, 250);
  });
  const unsubscribeConnected = eventService.subscribe('connected', () => {
    if (!eventService.isConnectedTo(projectId)) return;
    if (ignoreNextConnected) {
      ignoreNextConnected = false;
      if (storeRef.getQueryState().isError) {
        invalidateArchitecture(projectId, storeRef.dispatch);
      }

      return;
    }
    invalidateArchitecture(projectId, storeRef.dispatch);
  });
  const cleanup = () => {
    if (versionInvalidationTimer) clearTimeout(versionInvalidationTimer);
    unsubscribeArchitecture();
    unsubscribeConnected();
  };

  wiredProjects.set(projectId, { count: 1, cleanup });
  eventService.connect(projectId);

  return () => releaseArchitectureSSE(projectId);
}

export const architectureApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getArchitecture: builder.query<DiagramsResponse, string>({
      query: (projectId) => `/api/v1/projects/${projectId}/architecture`,
      providesTags: (_result, _error, projectId) => [
        { type: 'Architecture' as const, id: projectId },
      ],
    }),
    updateArchitectureDiagram: builder.mutation<
      UpdatedArchitectureDiagram,
      UpdateArchitectureDiagramArgs
    >({
      query: ({ projectId, diagramType, name, content }) => ({
        url: `/api/v1/projects/${projectId}/architecture`,
        method: 'PUT',
        body: { diagram_type: diagramType, name, content },
      }),
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: 'Architecture' as const, id: projectId },
        { type: 'ArtifactVersion' as const, id: `${projectId}:architecture` },
      ],
    }),
    getPendingArchitectureProposal: builder.query<ArchitecturePendingProposalResponse, string>({
      query: (projectId) => `/api/v1/projects/${projectId}/architecture/pending`,
      providesTags: (_result, _error, projectId) => [
        { type: 'ArchitectureProposal' as const, id: projectId },
      ],
    }),
    proposeArchitectureEdit: builder.mutation<{ status: string }, { projectId: string; prompt: string }>({
      query: ({ projectId, prompt }) => ({
        url: `/api/v1/projects/${projectId}/architecture/propose`,
        method: 'POST',
        body: { prompt },
      }),
    }),
    acceptPendingArchitectureProposal: builder.mutation<{ applied: unknown }, { projectId: string }>({
      query: ({ projectId }) => ({
        url: `/api/v1/projects/${projectId}/architecture/pending/accept`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: 'ArchitectureProposal' as const, id: projectId },
      ],
    }),
    rejectPendingArchitectureProposal: builder.mutation<void, string>({
      query: (projectId) => ({
        url: `/api/v1/projects/${projectId}/architecture/pending/reject`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, projectId) => [
        { type: 'ArchitectureProposal' as const, id: projectId },
      ],
    }),
  }),
});

export const {
  useGetArchitectureQuery: useGetArchitectureQueryBase,
  useLazyGetArchitectureQuery,
  useUpdateArchitectureDiagramMutation,
  useGetPendingArchitectureProposalQuery,
  useProposeArchitectureEditMutation,
  useAcceptPendingArchitectureProposalMutation,
  useRejectPendingArchitectureProposalMutation,
} = architectureApi;

interface GetArchitectureQueryOptions {
  skip?: boolean;
}

/** Shared RTK query plus reference-counted live updates for mounted consumers. */
export const useGetArchitectureQuery = (
  projectId: string,
  options?: GetArchitectureQueryOptions,
) => {
  const skip = options?.skip ?? false;
  const query = useGetArchitectureQueryBase(projectId, { skip });
  const dispatch = useAppDispatch();
  const queryStateRef = useRef<ArchitectureQueryState>(query);

  useEffect(() => {
    queryStateRef.current = query;
  }, [query]);

  useEffect(() => {
    if (!projectId || skip) return undefined;

    // The RTK cache may have been populated by the AI-overview artifact loader
    // before the first visual subscriber mounts. Wiring live updates must not
    // invalidate that fresh snapshot and issue the same GET a second time;
    // real architecture events and reconnects already refresh the cache.
    return wireArchitectureSSE(
      projectId,
      { dispatch, getQueryState: () => queryStateRef.current },
    );
  }, [dispatch, projectId, skip]);

  return query;
};
